import { useEffect, useRef, useState } from "react";
import { getRouteApi } from "@tanstack/react-router";
import { Masthead } from "../components/Masthead";
import { CatalogToolbar } from "../components/CatalogToolbar";
import { CatalogGrid } from "../components/CatalogGrid";
import { useCatalog } from "../data/useCatalog";
import { useFacets } from "../data/useFacets";
import { categoryLabel, sourceLabel, wholeNumber } from "../lib/labels";
import type { CatalogFilters, Shelf, SortMode } from "../lib/types";

const route = getRouteApi("/");

export function CatalogPage() {
  const search = route.useSearch();
  const navigate = route.useNavigate();
  const searchRef = useRef<HTMLInputElement>(null);

  // Immediate text in the field; committed to the URL (and the query key) on a
  // 200ms debounce so typing doesn't fire a request per keystroke.
  const [text, setText] = useState(search.q);
  useEffect(() => setText(search.q), [search.q]);
  useEffect(() => {
    const id = setTimeout(() => {
      if (text !== search.q) navigate({ search: (p) => ({ ...p, q: text }), replace: true });
    }, 200);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  const filters: CatalogFilters = {
    shelf: search.shelf,
    search: search.q,
    category: search.cat,
    source: search.src,
    sort: search.sort,
  };
  const catalog = useCatalog(filters);
  const facets = useFacets(search.shelf, search.src);

  // "/" focuses the search field from anywhere (Esc-to-clear lives on the input).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = document.activeElement?.tagName;
      const typing = tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
      if (e.key === "/" && !typing && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Reset scroll to the top whenever the active filter set changes (not on mount).
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    window.scrollTo({ top: 0 });
  }, [search.shelf, search.cat, search.src, search.sort, search.q]);

  // Changing shelf or source clears the category (it may not exist in the new
  // pool), matching the old app.js behavior.
  const setShelf = (shelf: Shelf) => navigate({ search: (p) => ({ ...p, shelf, cat: "" }), replace: true });
  const setSource = (src: string) => navigate({ search: (p) => ({ ...p, src, cat: "" }), replace: true });
  const setCategory = (cat: string) => navigate({ search: (p) => ({ ...p, cat }), replace: true });
  const setSort = (sort: SortMode) => navigate({ search: (p) => ({ ...p, sort }), replace: true });

  const emptyMessage = (() => {
    const criteria = search.q.trim() || categoryLabel(search.cat) || (search.src ? sourceLabel(search.src) : "");
    if (search.shelf === "lab" && !criteria) return "The recovery queue is clear.";
    if (criteria) return `Nothing here for “${criteria}”.`;
    return "Nothing matches the current filters.";
  })();

  const count = catalog.total;
  return (
    <>
      <Masthead variant="catalog" />
      <main>
        <CatalogToolbar
          searchText={text}
          onSearch={setText}
          searchRef={searchRef}
          shelf={search.shelf}
          onShelf={setShelf}
          category={search.cat}
          onCategory={setCategory}
          source={search.src}
          onSource={setSource}
          sort={search.sort}
          onSort={setSort}
          facets={facets.data}
        />
        <div className="results-row">
          <p className="results" role="status">
            {catalog.isLoading
              ? "Reading the catalog…"
              : `${wholeNumber.format(count)} ${count === 1 ? "game" : "games"}`}
          </p>
          {search.shelf === "multiplayer" && (
            <p className="section-note">
              Multiplayer is experimental — titles marked <strong>In progress</strong> can&rsquo;t be
              played yet.
            </p>
          )}
        </div>
        <CatalogGrid
          items={catalog.items}
          sort={search.sort}
          isLoading={catalog.isLoading}
          isError={catalog.isError}
          hasNextPage={!!catalog.hasNextPage}
          isFetchingNextPage={catalog.isFetchingNextPage}
          fetchNextPage={catalog.fetchNextPage}
          emptyMessage={emptyMessage}
        />
      </main>
      <footer className="colophon">
        <p>
          The games of FreeArcade.com, 1999–2013, recovered from Internet Archive captures and the
          Flashpoint archive. Original binaries only — Flash plays through Ruffle, Java through
          CheerpJ. Everything runs from this machine.
        </p>
        <p>Held marks applets awaiting missing assets or a runtime fix.</p>
        <p>Games remain © their authors. Kept for research and personal playback.</p>
      </footer>
    </>
  );
}
